/**
 * CheckboxField — React (web) implementation.
 *
 * A native checkbox drawn as the design-system box, beside a label built from
 * text and link segments. Pressing the label text toggles the box; pressing a
 * link opens it in a new tab and never toggles. Disabled stays focusable
 * (ADR-025 Rule 3) and links in it still open; unlike a native `disabled`
 * checkbox it is still sent when a plain HTML form posts.
 * All styling references the shared constants in CheckboxField.ts.
 */

import React, { useId, useRef, useState } from 'react';
import { focusRing } from '../../elements/focusRing';
import { ensureStyle } from '../../elements/focusStyle.web';
import { Icon } from '../Icon/Icon.web';
import { FieldError } from '../FieldError/FieldError.web';
import { hasFieldErrorMessage } from '../FieldError/FieldError';
import {
  checkboxFieldBoxStyleFor,
  checkboxFieldLabelText,
  isSafeCheckboxFieldHref,
  CHECKBOX_FIELD_BOX_SIZE,
  CHECKBOX_FIELD_BOX_RADIUS,
  CHECKBOX_FIELD_TICK_ICON,
  CHECKBOX_FIELD_TICK_SIZE,
  CHECKBOX_FIELD_FOCUS_RING_RADIUS,
  CHECKBOX_FIELD_ROW_GAP,
  CHECKBOX_FIELD_ERROR_GAP,
  CHECKBOX_FIELD_ERROR_INDENT,
  CHECKBOX_FIELD_LABEL_TYPOGRAPHY,
  CHECKBOX_FIELD_LABEL_COLOR,
} from './CheckboxField';
import type { CheckboxFieldLabelSegment } from './CheckboxField';

const CHECKBOX_FIELD_FOCUS_STYLE_ID = 'galapagos-checkbox-field-focus-visible';
const CHECKBOX_FIELD_INPUT_SELECTOR = '[data-galapagos-checkbox-field-input="true"]';
const CHECKBOX_FIELD_RING_SELECTOR = '[data-galapagos-checkbox-field-ring="true"]';

// The real <input> is invisible over the drawn box, so the ring is its own
// element right after the input, with the v7 ring radius (larger than the
// box's own). A sibling selector shows it for keyboard focus only (ADR-025
// Rule 2) without relying on `:has()`.
function ensureCheckboxFieldFocusStyle(): void {
  const outset = focusRing.width + focusRing.offset;
  ensureStyle(
    CHECKBOX_FIELD_FOCUS_STYLE_ID,
    `${CHECKBOX_FIELD_RING_SELECTOR} {` +
      ` display: none; position: absolute; inset: -${outset}px;` +
      ` border: ${focusRing.width}px solid ${focusRing.color};` +
      ` border-radius: ${CHECKBOX_FIELD_FOCUS_RING_RADIUS}px;` +
      ` pointer-events: none;` +
      ` }` +
      ` ${CHECKBOX_FIELD_INPUT_SELECTOR}:focus-visible + ${CHECKBOX_FIELD_RING_SELECTOR} {` +
      ` display: block;` +
      ` }`,
  );
}

export interface WebCheckboxFieldProps {
  labelSegments?: readonly CheckboxFieldLabelSegment[];
  /** Controlled checked state. */
  checked?: boolean;
  /** Initial checked state when uncontrolled. Defaults to unchecked. */
  defaultChecked?: boolean;
  disabled?: boolean;
  errorText?: string;
  name?: string;
  actionId?: string;
  onChange?: (next: boolean) => void;
  'data-testid'?: string;
  'aria-label'?: string;
}

export function CheckboxField({
  labelSegments = [],
  checked,
  defaultChecked = false,
  disabled = false,
  errorText,
  name,
  actionId = 'ui.checkbox-field.change',
  onChange,
  'data-testid': testId,
  'aria-label': ariaLabel,
}: WebCheckboxFieldProps) {
  ensureCheckboxFieldFocusStyle();

  const generatedId = useId();
  const inputId = testId ? `${testId}-input` : `checkbox-field-${generatedId}`;
  const labelId = `${inputId}-label`;
  const errorId = `${inputId}-error`;

  const [ownChecked, setOwnChecked] = useState(defaultChecked);
  const isChecked = checked ?? ownChecked;
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const hasError = hasFieldErrorMessage(errorText);
  const box = checkboxFieldBoxStyleFor({
    checked: isChecked,
    disabled,
    hasError,
    hovered: hovered && !disabled,
    focused: focused && !disabled,
  });

  const toggle = () => {
    if (disabled) return;
    const next = !isChecked;
    if (checked === undefined) setOwnChecked(next);
    onChange?.(next);
  };

  const handleLabelClick: React.MouseEventHandler<HTMLSpanElement> = (event) => {
    if (event.target instanceof Element && event.target.closest('a')) return;
    // A real click on the checkbox, so the toggle goes through the same click
    // and change events as a press on the box (and is captured for analytics).
    inputRef.current?.focus();
    inputRef.current?.click();
  };

  const accessibleName = ariaLabel ?? (checkboxFieldLabelText(labelSegments) || undefined);

  const boxStyle: React.CSSProperties = {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    boxSizing: 'border-box',
    width: CHECKBOX_FIELD_BOX_SIZE,
    height: CHECKBOX_FIELD_BOX_SIZE,
    borderRadius: CHECKBOX_FIELD_BOX_RADIUS,
    borderStyle: 'solid',
    borderWidth: box.borderWidth,
    borderColor: box.border,
    backgroundColor: box.background,
  };

  const labelStyle: React.CSSProperties = {
    fontFamily: CHECKBOX_FIELD_LABEL_TYPOGRAPHY.fontFamily,
    fontWeight: CHECKBOX_FIELD_LABEL_TYPOGRAPHY.fontWeight,
    fontSize: CHECKBOX_FIELD_LABEL_TYPOGRAPHY.fontSize,
    lineHeight: `${CHECKBOX_FIELD_LABEL_TYPOGRAPHY.lineHeight}px`,
    letterSpacing: CHECKBOX_FIELD_LABEL_TYPOGRAPHY.letterSpacing,
    color: CHECKBOX_FIELD_LABEL_COLOR,
    cursor: disabled ? 'not-allowed' : 'pointer',
  };

  const linkStyle: React.CSSProperties = {
    color: 'inherit',
    font: 'inherit',
    textDecoration: 'underline',
    cursor: 'pointer',
  };

  return (
    <div
      style={{ display: 'flex', flexDirection: 'column', gap: CHECKBOX_FIELD_ERROR_GAP }}
      data-testid={testId ? `${testId}-container` : undefined}
    >
      <div
        style={{ display: 'flex', alignItems: 'flex-start', gap: CHECKBOX_FIELD_ROW_GAP }}
        // Mouse only: a touch tap fires pointerenter too, and the hover state
        // would stick after the finger lifts.
        onPointerEnter={(event) => {
          if (event.pointerType === 'mouse') setHovered(true);
        }}
        onPointerLeave={() => setHovered(false)}
      >
        <span style={boxStyle} data-testid={testId ? `${testId}-box` : undefined}>
          <input
            ref={inputRef}
            data-analytics-action={actionId}
            data-galapagos-checkbox-field-input="true"
            type="checkbox"
            id={inputId}
            name={name ?? inputId}
            checked={isChecked}
            onChange={toggle}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            // Disabled stays focusable (ADR-025 Rule 3): aria-disabled instead
            // of the native attribute. The input is always controlled, so a
            // press that `toggle` ignores is reverted by React; cancelling the
            // click as well would make the browser restore the wrong value.
            aria-disabled={disabled || undefined}
            aria-invalid={hasError}
            aria-describedby={hasError ? errorId : undefined}
            {...(ariaLabel !== undefined
              ? { 'aria-label': ariaLabel }
              : accessibleName
                ? { 'aria-labelledby': labelId }
                : {})}
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              margin: 0,
              opacity: 0,
              cursor: disabled ? 'not-allowed' : 'pointer',
            }}
            data-testid={testId}
          />
          <span aria-hidden="true" data-galapagos-checkbox-field-ring="true" />
          {isChecked ? (
            <Icon
              name={CHECKBOX_FIELD_TICK_ICON}
              size={CHECKBOX_FIELD_TICK_SIZE}
              color={box.tick}
              aria-hidden
            />
          ) : null}
        </span>
        {/* analytics-exempt: forwarded-wrapper */}
        <span
          id={labelId}
          style={labelStyle}
          onClick={handleLabelClick}
          data-testid={testId ? `${testId}-label` : undefined}
        >
          {labelSegments.map(
            ({ text, href, actionId: linkActionId = 'ui.checkbox-field.link' }, index) =>
              isSafeCheckboxFieldHref(href) ? (
                <a
                  key={index}
                  data-analytics-action={linkActionId}
                  data-testid={testId ? `${testId}-link-${index}` : undefined}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={linkStyle}
                >
                  {text}
                </a>
              ) : (
                <React.Fragment key={index}>{text}</React.Fragment>
              ),
          )}
        </span>
      </div>
      {/* Always present, so the error is announced when it appears inside it. */}
      <div aria-live="polite" style={{ paddingLeft: CHECKBOX_FIELD_ERROR_INDENT }}>
        <FieldError
          live={false}
          message={errorText}
          id={errorId}
          data-testid={testId ? `${testId}-error` : undefined}
        />
      </div>
    </div>
  );
}
